import {
  coordsOf,
  DEFAULT_TIMEZONE,
  endMinute,
  localDate,
  PERSONAL_PREFIX,
  startMinute,
  zonedIso,
  type Opportunity,
} from '../../shared/opportunity';
import { areaForCoords } from './travel';

/**
 * Events the user adds themselves.
 *
 * A Climate Week schedule is not only Climate Week. Dinner, a flight home, the
 * hour blocked out to actually do some work — none of it is in the listings, and
 * all of it decides whether the rest of the day is possible. Leaving it out
 * made every conflict and every travel estimate optimistic by however much of
 * the day the user had not told us about.
 *
 * They are read as full opportunity records rather than a parallel type, so
 * every piece of machinery already built — lanes, overlaps, travel legs,
 * attendance trimming, the .ics export — works on them without knowing they
 * are different. The one thing that distinguishes them is the id prefix, which
 * is checked by `isPersonal` where the presentation genuinely differs.
 */

export const isPersonal = (event: { id: string }): boolean => event.id.startsWith(PERSONAL_PREFIX);

export const newPersonalId = (): string =>
  `${PERSONAL_PREFIX}${globalThis.crypto?.randomUUID?.() ?? String(Date.now())}`;

/** What the form collects. Everything but title, day and start is optional. */
export interface PersonalDraft {
  id: string;
  title: string;
  dayKey: string;
  startMinutes: number;
  endMinutes: number;
  /** Free text. A chosen suggestion also supplies coordinates. */
  venue: string;
  coords: [number, number] | null;
  description: string;
}

export function emptyDraft(dayKey: string, startMinutes: number): PersonalDraft {
  return {
    id: newPersonalId(),
    title: '',
    dayKey,
    startMinutes,
    endMinutes: startMinutes + DEFAULT_LENGTH_MINUTES,
    venue: '',
    coords: null,
    description: '',
  };
}

/** A click on empty schedule claims the half-hour it landed in. */
export const DEFAULT_LENGTH_MINUTES = 30;
export const SLOT_MINUTES = 30;

/** Rounds a clicked minute down to the half-hour it falls inside. */
export const slotFor = (minutes: number) => Math.floor(minutes / SLOT_MINUTES) * SLOT_MINUTES;

/** A draft is only saveable once it has the three required fields. */
export const isComplete = (draft: PersonalDraft): boolean =>
  draft.title.trim().length > 0 &&
  Boolean(draft.dayKey) &&
  Number.isFinite(draft.startMinutes) &&
  draft.endMinutes > draft.startMinutes;

/**
 * Turns a draft into something the rest of the app can treat as an event.
 *
 * The fields with no personal equivalent are given the value that makes the
 * existing behaviour correct rather than a placeholder: open to the public,
 * because you never need permission to attend your own event, and no topics
 * or goals, because inventing them would put the event under filters the user
 * never chose.
 */
export function toOpportunity(draft: PersonalDraft): Opportunity {
  const venue = draft.venue.trim();
  const description = draft.description.trim();
  const title = draft.title.trim();
  const area = draft.coords ? areaForCoords(draft.coords) : null;

  return {
    id: draft.id,
    sourceProvider: 'personal',
    sourceUrl: '',
    registrationUrl: null,
    organizationId: null,
    organizationName: '',
    name: title,
    summary: description,
    description,
    startsAt: zonedIso(draft.dayKey, draft.startMinutes, DEFAULT_TIMEZONE),
    endsAt: zonedIso(draft.dayKey, draft.endMinutes, DEFAULT_TIMEZONE),
    isAllDay: false,
    timezone: DEFAULT_TIMEZONE,
    format: 'in_person',
    eventType: null,
    rsvpType: 'open_to_public',
    category: null,
    topics: [],
    goals: [],
    neighborhood: area,
    // Only a chosen suggestion is an address: free text may be "Sam's flat",
    // which is not somewhere a map can send anyone.
    locationType: draft.coords ? 'address' : null,
    address: venue || null,
    latitude: draft.coords?.[0] ?? null,
    longitude: draft.coords?.[1] ?? null,
    isOutdoor: null,
    isOfficial: false,
    isPaid: null,
    priceAmount: null,
    priceCurrency: null,
    createdAt: '',
    updatedAt: '',
  };
}

/** Recovers the form's fields from a stored event, for editing. */
export function toDraft(event: Opportunity): PersonalDraft {
  const start = startMinute(event) ?? 0;
  return {
    id: event.id,
    title: event.name,
    dayKey: localDate(event),
    startMinutes: start,
    endMinutes: endMinute(event) ?? start + DEFAULT_LENGTH_MINUTES,
    venue: event.address ?? '',
    coords: coordsOf(event),
    description: event.description ?? '',
  };
}
